import { isNil, isNotNil } from 'ramda';

type TreeData = {
  profile: {
    nid: number;
    title: string;
    firstQuestion: number;
  };
  questions: Record<number, {
    nid: number;
    question: string;
    description: string;
    answers: number[];
    openInNewPan: boolean;
  }>;
  answers: Record<number, {
    nid: number;
    answer: string;
    shortAnswer: string;
    description?: string;
    nextQuestion?: number;
    isChecked: boolean;
  }>;
}


const treeStore = {
  data: {} as TreeData,
  breadcrumb: [0] as TreeData['answers'][number]['nid'][],
  answerBuffer: null as number | null,
  resultPath: null as string | null,
  ready: false,
  tour_page: null as string | null,

  mode: 'free' as 'submit' | 'free',
  debug: false,

  init() {
    const path = window.location.hash.substring(1).split('-').map(Number);
    if (path.length > 0 && window.location.hash.length > 0) {
      this.breadcrumb = [0, ...path];
    }
  },

  loadTour({ nid, firstQuestion, title, tour_page }: { nid: string, firstQuestion: string, title: string, tour_page: string }) {
    if (nid === '' || firstQuestion === '') return;
    this.tour_page = tour_page;
    fetch(`/api/tour/${nid}`)
      .then(response => response.json())
      .then(data => {
        const questions =  data.questions.reduce((acc: TreeData['questions'], question: TreeData['questions'][number]) => {
          return {...acc, [question.nid]: question};
        }, {});
        const answers =  data.answers.reduce((acc: TreeData['answers'], answer: TreeData['answers'][number]) => {
          return {...acc, [answer.nid]: answer};
        }, {});

        this.data = {
          ...data,
          questions: questions,
          answers: {
            '0': {
              nid: 0,
              shortAnswer: title,
              nextQuestion: firstQuestion,
              isChecked: true,
            },
            ...answers
          },
        };
        this.ready = true;
        this.setResultPath(this.breadcrumb[this.breadcrumb.length - 1]);
      })
      .catch(error => {
        console.error(error);
      });
  },

  getQuestion(answerNid: number) {
    const answer = this.getAnswer(answerNid);
    if (!answer?.nextQuestion) return null;
    return this.data.questions[answer.nextQuestion];
  },

  getAnswer(answerNid: number) {
    if (!this.data.answers) return;
    return this.data.answers[answerNid];
  },

  getAnswers(answerNid: number) {
    const answers = this.getQuestion(answerNid)?.answers;
    if (!answers) return [];
    return answers.map(answerNid => this.getAnswer(answerNid));
  },

  continue() {
    if (!this.answerBuffer) return;

    this.setResultPath(this.answerBuffer);
    if (this.resultPath !== null) {
      window.location.href = this.resultPath;
    } else {
      this.breadcrumb.push(this.answerBuffer);
      this.answerBuffer = null;
      this.updatePath();
    }
  },

  back(answerNid?: number) {
    if (this.mode === 'submit') {
      if (this.breadcrumb.length === 1) {
        window.location.href = this.tour_page ?? '';
      }
      this.breadcrumb.pop();
    } else if (this.mode === 'free' && isNil(answerNid)) {
      const questions = this.breadcrumb.map(id => this.getQuestion(id));
      const lastPanelQuestion = questions.findLastIndex(question => question?.openInNewPan);

      if (lastPanelQuestion !== -1) {
        this.breadcrumb = this.breadcrumb.slice(0, lastPanelQuestion);
      } else {
        window.location.href = this.tour_page ?? '';
      }
    } else if (this.mode === 'free' && isNotNil(answerNid)) {
      const index = this.breadcrumb.indexOf(answerNid);
      if (index !== -1) {
        const question = this.getQuestion(answerNid);
        this.breadcrumb = this.breadcrumb.slice(0, question?.openInNewPan ? index : index + 1);
      }
    }

    this.answerBuffer = null;
    this.updatePath();
  },

  updateAnswerBuffer(answerNid: number, parentAnswerNid: number) {

    this.answerBuffer = answerNid;

    if (this.mode === 'free') {
      // Rewind breadcrumb if already answered
      const alreadyAnsweredIndex = this.breadcrumb.findIndex(item => this.getQuestion(parentAnswerNid)?.answers.map(Number).includes(item));
      if (alreadyAnsweredIndex !== -1) {
        this.breadcrumb = this.breadcrumb.slice(0, alreadyAnsweredIndex);
      }

      // Auto continue if in free mode
      const question = this.getQuestion(answerNid);
      const answer = this.getAnswer(answerNid);
      if (!question?.openInNewPan && answer?.nextQuestion !== null) {
        this.continue();
      }
    }
  },

  updatePath() {
    window.history.pushState(null, '', `#${this.breadcrumb.slice(1).join('-')}`);
  },

  setResultPath(lastAnswerNid: number) {
    const lastAnswer = this.getAnswer(lastAnswerNid);
    if (lastAnswer?.nextQuestion === null) {
      this.resultPath = `/tree/${this.data.profile.nid}/results/${this.breadcrumb.slice(1).join('-')}-${lastAnswerNid}`.replace('/-', '/');
    }
  },

  canShowQuestion(answerNid: number) {
    if (this.breadcrumb.length === 1 || this.debug) return true;
    const currentIndex = this.breadcrumb.indexOf(answerNid);
    const questions = this.breadcrumb.map(id => this.getQuestion(id));
    const lastPanelQuestion = questions.findLastIndex(question => question?.openInNewPan);
    return currentIndex >= lastPanelQuestion;
  },
};

export default treeStore;
